/**
 * The Coverage HUD's one engine, ported from the production Widget Host (ensureCombo, recompute and the live
 * pricing). It works on features as the data plane serves them (GeoJSON, WGS84) and never touches the map: the
 * tenant turns its results into overlays. Pure, tested in Node.
 */
import type { Axis, LiveAnalysis } from './analyses';

export interface HudFeature {
  properties: Readonly<Record<string, unknown>> | null;
  geometry: { type: string; coordinates?: unknown } | null;
}

/** A WGS84 point, or null when the feature has no point position. */
export function pointOf(f: HudFeature): [number, number] | null {
  const g = f.geometry;
  if (!g || g.type !== 'Point' || !Array.isArray(g.coordinates)) return null;
  const [x, y] = g.coordinates as unknown[];
  return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
}

/** Keys compare as text: one service's integer id and another's text id are the same feature. */
export const idOf = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';

const n = (v: unknown): number => {
  const x = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(x) ? x : 0;
};

/** The value an axis shows: what the reader picked for this analysis, else the axis's own init. */
export function axisValue(axes: readonly Axis[], picked: Readonly<Record<string, string>> | undefined, key: string) {
  const v = picked?.[key];
  if (v !== undefined) return v;
  return axes.find((a) => a.key === key)?.init ?? '';
}

/** The combo string: the axis values joined with '_' in table order (the published field-name order). */
export const comboOf = (axes: readonly Axis[], picked: Readonly<Record<string, string>> | undefined): string =>
  axes.map((a) => axisValue(axes, picked, a.key)).join('_');

/** One hop, pole to pole: glide ratio x (launch height - landing height), launch being below the cross-beam. */
export function reachFor(ratio: number, height: number, beam = 0.5, landing = 2): number {
  return ratio > 0 ? Math.max(0, ratio * (height - beam - landing)) : 0;
}

/** Poles for a gap: the edge trees are the first and last stepping stones. The 1e-9 keeps a gap that is an exact
 *  multiple of the reach from costing an extra pole through floating-point noise. */
export const polesFor = (gap: number, reach: number): number =>
  reach > 0 && gap > 0 ? Math.max(1, Math.ceil(gap / reach - 1e-9) - 1) : 0;

export interface SelRow {
  rank: number;
  reach: number;
  gap: number;
  type: string;
  point: [number, number] | null;
}

export interface ComboData {
  /** Rankable selection features by key. */
  hubs: Map<string, SelRow>;
  /** Target key -> the links that reach it, with their level. */
  idx: Map<string, { hub: string; v: number }[]>;
}

/**
 * One combo's candidates and links. rank > 0, NOT "is not null": publishing coerces null numerics to 0, so only
 * ">0" excludes both. `rankMax` is the other half of that trap: a high sentinel rank means "never selectable" and
 * stays out of the candidate set here, once.
 */
export function comboData(
  a: LiveAnalysis,
  combo: string,
  selection: readonly HudFeature[],
  links: readonly HudFeature[],
): ComboData {
  const K = a.keys;
  const rankF = a.prefix.rank + combo;
  const sizeF = a.prefix.size + combo;
  const valF = a.prefix.value + combo;
  const hubs = new Map<string, SelRow>();
  for (const f of selection) {
    const p = f.properties ?? {};
    const rank = n(p[rankF]);
    if (!(rank > 0)) continue;
    if (a.rankMax !== undefined && !(rank < a.rankMax)) continue;
    hubs.set(idOf(p[K.sel]), {
      rank,
      reach: n(p[sizeF]),
      gap: K.gap ? n(p[K.gap]) : 0,
      type: K.kind ? idOf(p[K.kind]) : '',
      point: pointOf(f),
    });
  }
  const idx = new Map<string, { hub: string; v: number }[]>();
  for (const f of links) {
    const p = f.properties ?? {};
    const v = n(p[valF]);
    if (!(v > 0)) continue;
    const d = idOf(p[K.linkTgt]);
    const arr = idx.get(d) ?? [];
    arr.push({ hub: idOf(p[K.linkSel]), v });
    idx.set(d, arr);
  }
  return { hubs, idx };
}

export interface HudFigures {
  served: number;
  coverage: number;
  invest: number;
  /** The summed size field over the selection (production keeps this as `units`). */
  units: number;
  /** The selected crossings' gaps, so the render can re-price for any pole height or rate. */
  gaps: number[];
  perDwell: number;
  perSite: number;
  /** The rankable candidates: N's ceiling. */
  siteMax: number;
  /** Each target's effective level (0 = not reached at the threshold). */
  bands: Map<string, number>;
  /** The selected candidates' keys. */
  selected: Set<string>;
}

/** Fund the top `n`, then read each target's best level from any funded candidate, counted at `minBars`. */
export function recompute(
  a: LiveAnalysis,
  data: ComboData,
  targetIds: readonly string[],
  n: number,
  minBars: number,
): HudFigures {
  const selected = new Set<string>();
  data.hubs.forEach((h, id) => {
    if (h.rank >= 1 && h.rank <= n) selected.add(id);
  });
  const bands = new Map<string, number>();
  let served = 0;
  for (const d of targetIds) {
    let band = 0;
    for (const l of data.idx.get(d) ?? []) if (selected.has(l.hub) && l.v > band) band = l.v;
    const eff = band >= minBars ? band : 0;
    if (eff) served++;
    bands.set(d, eff);
  }
  let sizeSum = 0;
  const gaps: number[] = [];
  selected.forEach((id) => {
    const h = data.hubs.get(id);
    sizeSum += h?.reach ?? 0;
    if (h?.gap) gaps.push(h.gap);
  });
  const units = a.cost.per === 'size' ? sizeSum : n;
  const invest = units * a.cost.rate;
  return {
    served,
    coverage: served / (a.universe || 1),
    invest,
    units: sizeSum,
    gaps,
    perDwell: served > 0 ? invest / served : 0,
    perSite: n > 0 ? served / n : 0,
    siteMax: data.hubs.size,
    bands,
    selected,
  };
}

/** The live pricing of a selection at a pole height and rate (no re-query: the gaps are already in hand). */
export function livePrice(gaps: readonly number[], reach: number, rate: number) {
  const perPoles = gaps.map((g) => polesFor(g, reach));
  const units = perPoles.reduce((s, p) => s + p, 0);
  return {
    units,
    invest: units * rate,
    maxPoles: perPoles.length ? Math.max(...perPoles) : 0,
    avgLen: gaps.length ? gaps.reduce((s, g) => s + g, 0) / gaps.length : 0,
    maxLen: gaps.length ? Math.max(...gaps) : 0,
  };
}

/** N's meter: 28 ticks, the run up to the knob lit. */
export const METER_TICKS = 28;
export function meterTicks(
  pct: number,
  on = 'color-mix(in srgb,var(--mm-accent,#27e3c4) 60%,transparent)',
): { bg: string; h: number }[] {
  const red = Math.round(pct * METER_TICKS);
  return Array.from({ length: METER_TICKS }, (_, i) => ({
    bg: i <= red ? on : 'rgba(255,255,255,.16)',
    h: 6 + (i / METER_TICKS) * 8,
  }));
}

export const fmtInt = (v: number): string => Math.round(v).toLocaleString();
export const fmtMoney = (v: number): string => '$' + (v / 1e6).toFixed(1) + 'M';
